import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixCaptcharatorV1CaptchaEntity from './captcharator-v1-captcha-entity-captcha.http.js';

export interface CaptchaEntity {}

export interface CaptchaRequest {
  /** The CAPTCHA token to authorize. */
  token?: string;
}

export interface CaptchaResponse {
  /** Value is `true` when authorization is successful. */
  success?: boolean;
  /** Error information. */
  errors?: Errors;
}

export interface Errors {
  /** ID of error. */
  errorId?: number;
}

export interface AssessmentResponse {
  assessment?: string;
}

/**
 * Authorizes a CAPTCHA token.
 *
 * Following CAPTCHA verification on the client side, you must authorize the generated CAPTCHA
 * token in the backend.
 *
 * `Authorize` checks if the token is valid, making sure it was not tampered with or timed out.
 *
 * If you're developing a Wix site or Blocks app, call this method when working with the
 * [Wix reCAPTCHA](https://dev.wix.com/docs/velo/api-reference/$w/captcha/introduction) element.
 *
 * If CAPTCHA token authorization fails, the method returns an error message containing a status code.
 * The following table lists the possible HTTP error status codes, based on
 * [RFC 2616](https://datatracker.ietf.org/doc/html/rfc2616#section-10):
 *
 *
 * | Status Code | Name | Description |
 * |---|---|---|
 * | 400 | Bad Request | The request could not be understood by the server. This could occur for a number of reasons, such as: <ul> <li>The request was sent without a token.</li> <li>The token is invalid.</li> <li>The token has timed out.</li> </ul> |
 * | 401 | Unauthenticated | No user identity found in passed request. |
 * | 500 | Internal Server Error | The server encountered an unexpected condition, such as a missing or invalid private CAPTCHA key. |
 * | 503 | Unavailable | The service is unavailable due to one of the following: <ul> <li>Throttled error: Server overload due to more than the allowed requests in a given time frame.</li> <li>Request failed: No response following 10 retries with a 1-second interval.</li> </ul> |
 * @param token - The CAPTCHA token to authorize.
 * @public
 * @documentationMaturity preview
 * @requiredField token
 * @fqn com.wixpress.vi.captcharator.CaptchaService.authorize
 */
export async function authorize(
  token: string
): Promise<NonNullablePaths<CaptchaResponse, `success` | `errors.errorId`, 3>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ token: token });

  const reqOpts = ambassadorWixCaptcharatorV1CaptchaEntity.authorize(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { token: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['token']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
